import { useState, useEffect, useRef, useCallback } from 'react'
import {
  X,
  Send,
  Bot,
  User,
  CheckCircle,
  Loader2,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Sparkles,
  Pencil,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

interface ChatbotQuestion {
  id: string
  label: string
  type: 'text' | 'date' | 'time' | 'number' | 'email' | 'phone' | 'select' | 'textarea'
  options?: string[]
  required: boolean
  field: 'subject' | 'message' | 'type_code' | 'category_code' | 'severity_code'
}

interface ChatbotConfig {
  enabled: boolean
  welcome_message: string
  questions: ChatbotQuestion[]
}

interface ChatMessage {
  role: 'bot' | 'user'
  text: string
}

/** Map of question.id → value collected from extraction or user answers.
 *  Keyed by question ID (not field) so multiple questions with the same ERP field
 *  each keep their own answer. Mapped to ERP fields only at ticket-creation time. */
type CollectedAnswers = Record<string, string>

interface Props {
  onClose: () => void
  onTicketCreated: () => void
}

// Browser SpeechRecognition types (not in all TypeScript DOM libs)
interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number
  readonly results: SpeechRecognitionResultList
}

interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onstart: (() => void) | null
  onresult: ((ev: SpeechRecognitionEvent) => void) | null
  onerror: ((ev: Event) => void) | null
  onend: (() => void) | null
}

declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognitionInstance
    webkitSpeechRecognition: new () => SpeechRecognitionInstance
  }
}

// ------------------------------------------------------------------
// Answer validation
// ------------------------------------------------------------------

interface ValidationResult {
  valid: boolean
  /** Normalized value to store (e.g. "08:30" from "8h30") */
  normalized: string
  /** i18n key for the error message shown when invalid */
  errorKey?: string
}

function validateAnswer(question: ChatbotQuestion, raw: string): ValidationResult {
  const v = raw.trim()
  switch (question.type) {
    case 'time': {
      // Accepts: "8h30", "8:30", "08:30", "8h", "20:00", "8 h 30", "8 heures 30"
      const m =
        v.match(/^(\d{1,2})\s*[hH:]\s*(\d{0,2})(?:\s*(?:min(?:utes?)?)?)?$/i) ||
        v.match(/^(\d{1,2})\s*heures?\s*(\d{0,2})/i)
      if (!m) return { valid: false, normalized: v, errorKey: 'chatbot.validationTime' }
      const h = parseInt(m[1], 10)
      const min = parseInt(m[2] || '0', 10)
      if (h > 23 || min > 59)
        return { valid: false, normalized: v, errorKey: 'chatbot.validationTime' }
      return {
        valid: true,
        normalized: `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`,
      }
    }
    case 'date': {
      // HTML date input already produces ISO format; allow ISO or common FR/EN text dates
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const d = new Date(v)
        if (isNaN(d.getTime()))
          return { valid: false, normalized: v, errorKey: 'chatbot.validationDate' }
        return { valid: true, normalized: v }
      }
      // Try DD/MM/YYYY or DD-MM-YYYY
      const dmY = v.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
      if (dmY) {
        const iso = `${dmY[3]}-${String(dmY[2]).padStart(2, '0')}-${String(dmY[1]).padStart(2, '0')}`
        const d = new Date(iso)
        if (!isNaN(d.getTime())) return { valid: true, normalized: iso }
      }
      return { valid: false, normalized: v, errorKey: 'chatbot.validationDate' }
    }
    case 'number': {
      const n = parseFloat(v.replace(',', '.').replace(/\s/g, ''))
      if (isNaN(n)) return { valid: false, normalized: v, errorKey: 'chatbot.validationNumber' }
      return { valid: true, normalized: String(n) }
    }
    case 'email': {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v))
        return { valid: false, normalized: v, errorKey: 'chatbot.validationEmail' }
      return { valid: true, normalized: v.toLowerCase() }
    }
    case 'phone': {
      const digits = v.replace(/[\s\.\-\(\)]/g, '')
      if (!/^\+?\d{8,15}$/.test(digits))
        return { valid: false, normalized: v, errorKey: 'chatbot.validationPhone' }
      return { valid: true, normalized: v }
    }
    default:
      return { valid: true, normalized: v }
  }
}

// ------------------------------------------------------------------
// Phase type
// ------------------------------------------------------------------

type Phase =
  | 'initial'
  | 'extracting'
  | 'chat'
  | 'reviewing'
  | 'review-input'
  | 'review-extracting'
  | 'summary'
  | 'submitting'
  | 'done'

// ------------------------------------------------------------------
// Component
// ------------------------------------------------------------------

export default function TicketChatbot({ onClose, onTicketCreated }: Props) {
  const { t, i18n } = useTranslation()

  const [config, setConfig] = useState<ChatbotConfig | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(true)

  const [messages, setMessages] = useState<ChatMessage[]>([])
  // Questions not answered by AI extraction → still need to ask the user
  const [pendingQuestions, setPendingQuestions] = useState<ChatbotQuestion[]>([])
  const [currentPendingIndex, setCurrentPendingIndex] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [initialDescription, setInitialDescription] = useState('')
  // Raw description kept for ticket body construction (initialDescription is cleared after submit)
  const [rawDescription, setRawDescription] = useState('')
  const [reviewInput, setReviewInput] = useState('')
  // All collected field values (from extraction + user answers)
  const [answers, setAnswers] = useState<CollectedAnswers>({})
  // Ref always in sync with answers — prevents stale-closure bugs in useCallback
  const answersRef = useRef<CollectedAnswers>({} as CollectedAnswers)
  useEffect(() => {
    answersRef.current = answers
  }, [answers])

  const [phase, setPhase] = useState<Phase>('initial')
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Voice state
  const [voiceMode, setVoiceMode] = useState(false)
  const [voiceSupported, setVoiceSupported] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null)
  const voiceModeRef = useRef(false)
  const phaseRef = useRef<Phase>('initial')
  const pendingVoiceSubmitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // In initial phase: auto-submit fires after INITIAL_SILENCE_MS of silence (longer than chat phase)
  const pendingInitialSubmitRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const INITIAL_SILENCE_MS = 2500
  const handleSendRef = useRef<() => void>(() => {})
  // Points to the right state setter depending on current phase (initial → initialDescription, chat → inputValue)
  const voiceInputSetterRef = useRef<(v: string) => void>((v) => setInputValue(v))
  // Accumulates confirmed transcripts across multiple STT sessions in the initial and review-input phases.
  // Prevents each new recognition instance from overwriting previous sentences.
  const voiceAccumulatedRef = useRef('')
  // Stable refs for review/summary phase handlers (updated each render)
  const handleReviewChoiceRef = useRef<(yes: boolean, content?: string) => void>(() => {})
  const handleReviewInputSubmitRef = useRef<() => Promise<void>>(async () => {})
  const handleReviewVoiceAnswerRef = useRef<() => void>(() => {})
  const runReviewExtractionRef = useRef<(text: string) => Promise<void>>(async () => {})
  const handleSummaryVoiceRef = useRef<() => void>(() => {})
  const handleConfirmRef = useRef<() => void>(() => {})
  // When true, handleSend returns to summary directly (skips reviewing phase — used by pencil edits)
  const skipReviewPhaseRef = useRef(false)

  // ── Browser support check ─────────────────────────────────────────
  useEffect(() => {
    const API = window.SpeechRecognition || window.webkitSpeechRecognition
    setVoiceSupported(!!(API && window.speechSynthesis))
  }, [])

  useEffect(() => {
    voiceModeRef.current = voiceMode
  }, [voiceMode])
  useEffect(() => {
    phaseRef.current = phase
  }, [phase])

  // Keep voiceInputSetterRef in sync with current phase
  useEffect(() => {
    if (phase === 'initial') voiceInputSetterRef.current = (v: string) => setInitialDescription(v)
    else if (phase === 'review-input')
      voiceInputSetterRef.current = (v: string) => setReviewInput(v)
    else voiceInputSetterRef.current = (v: string) => setInputValue(v)
  }, [phase])

  // ── TTS ───────────────────────────────────────────────────────────
  const speak = useCallback(
    (text: string, onEnd?: () => void) => {
      if (!voiceModeRef.current || !window.speechSynthesis) {
        onEnd?.()
        return
      }
      window.speechSynthesis.cancel()
      const utt = new SpeechSynthesisUtterance(text)
      utt.lang = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
      utt.rate = 1.0
      utt.onstart = () => setIsSpeaking(true)
      utt.onend = () => {
        setIsSpeaking(false)
        onEnd?.()
      }
      utt.onerror = () => {
        setIsSpeaking(false)
        onEnd?.()
      }
      window.speechSynthesis.speak(utt)
    },
    [i18n.language]
  )

  // ── STT ───────────────────────────────────────────────────────────
  const stopListening = useCallback(() => {
    if (pendingVoiceSubmitRef.current) {
      clearTimeout(pendingVoiceSubmitRef.current)
      pendingVoiceSubmitRef.current = null
    }
    if (pendingInitialSubmitRef.current) {
      clearTimeout(pendingInitialSubmitRef.current)
      pendingInitialSubmitRef.current = null
    }
    try {
      recognitionRef.current?.stop()
    } catch {
      /* ignore */
    }
    recognitionRef.current = null
    setIsListening(false)
  }, [])

  // force=true allows manual mic click even when voice mode is off
  const startListening = useCallback(
    (force = false) => {
      const API = window.SpeechRecognition || window.webkitSpeechRecognition
      if (!API || (!voiceModeRef.current && !force)) return
      // Stop only the current recognition instance — do NOT cancel pending submit timers.
      // (stopListening() would clear them; startListening must not interfere with timers.)
      if (pendingVoiceSubmitRef.current) {
        clearTimeout(pendingVoiceSubmitRef.current)
        pendingVoiceSubmitRef.current = null
      }
      try {
        recognitionRef.current?.stop()
      } catch {
        /* ignore */
      }
      recognitionRef.current = null
      const recognition = new API()
      recognition.lang = i18n.language === 'fr' ? 'fr-FR' : 'en-US'
      recognition.interimResults = true
      recognition.continuous = false
      recognition.maxAlternatives = 1
      let finalTranscript = ''
      recognition.onstart = () => setIsListening(true)
      recognition.onresult = (ev: SpeechRecognitionEvent) => {
        let interim = ''
        finalTranscript = ''
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          if (ev.results[i].isFinal) finalTranscript += ev.results[i][0].transcript
          else interim += ev.results[i][0].transcript
        }
        if (phaseRef.current === 'initial' || phaseRef.current === 'review-input') {
          // Show accumulated previous sentences + current transcript (live preview)
          const current = (finalTranscript || interim).trim()
          const base = voiceAccumulatedRef.current
          if (base) {
            const sep = /[.!?,;]$/.test(base.trimEnd()) ? ' ' : '. '
            voiceInputSetterRef.current(base + sep + current)
          } else {
            voiceInputSetterRef.current(current)
          }
          // Cancel any pending auto-submit — user is still speaking
          if (pendingInitialSubmitRef.current) {
            clearTimeout(pendingInitialSubmitRef.current)
            pendingInitialSubmitRef.current = null
          }
        } else {
          voiceInputSetterRef.current(finalTranscript || interim)
        }
      }
      recognition.onend = () => {
        setIsListening(false)
        recognitionRef.current = null
        if (!voiceModeRef.current) return
        if (phaseRef.current === 'initial' || phaseRef.current === 'review-input') {
          if (finalTranscript.trim()) {
            // Commit this sentence to the accumulator with punctuation separator
            const base = voiceAccumulatedRef.current
            if (base) {
              const sep = /[.!?,;]$/.test(base.trimEnd()) ? ' ' : '. '
              voiceAccumulatedRef.current = base + sep + finalTranscript.trim()
            } else {
              voiceAccumulatedRef.current = finalTranscript.trim()
            }
            // Restart immediately so the user can continue with another sentence
            startListening()
            // (Re)schedule auto-submit after prolonged silence
            if (pendingInitialSubmitRef.current) clearTimeout(pendingInitialSubmitRef.current)
            pendingInitialSubmitRef.current = setTimeout(() => {
              pendingInitialSubmitRef.current = null
              handleSendRef.current()
            }, INITIAL_SILENCE_MS)
          }
          // If no transcript (pure silence), do nothing — wait for user to speak
        } else if (finalTranscript.trim()) {
          // Chat / reviewing phase: short fixed delay then auto-submit
          pendingVoiceSubmitRef.current = setTimeout(() => handleSendRef.current(), 600)
        }
      }
      recognition.onerror = () => {
        setIsListening(false)
        recognitionRef.current = null
      }
      recognitionRef.current = recognition
      recognition.start()
    },
    [i18n.language]
  )

  // ── Toggle voice mode ─────────────────────────────────────────────
  const toggleVoiceMode = useCallback(() => {
    const next = !voiceModeRef.current
    setVoiceMode(next)
    if (!next) {
      window.speechSynthesis?.cancel()
      stopListening()
    }
  }, [stopListening])

  // ── Cleanup ───────────────────────────────────────────────────────
  useEffect(
    () => () => {
      window.speechSynthesis?.cancel()
      stopListening()
    },
    [stopListening]
  )

  // ── Load config + show welcome ────────────────────────────────────
  useEffect(() => {
    api
      .getChatbotConfig()
      .then((cfg) => {
        setConfig(cfg)
        const welcome = cfg.welcome_message || t('chatbot.defaultWelcome')
        const msgs: ChatMessage[] = [
          { role: 'bot', text: welcome },
          { role: 'bot', text: t('chatbot.describePrompt') },
        ]
        setMessages(msgs)
      })
      .catch(() => setMessages([{ role: 'bot', text: t('chatbot.loadError') }]))
      .finally(() => setLoadingConfig(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Scroll to bottom ──────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // ── Auto-focus input ──────────────────────────────────────────────
  useEffect(() => {
    if ((phase === 'initial' || phase === 'chat') && !voiceMode) {
      setTimeout(() => (inputRef.current as HTMLElement | null)?.focus(), 100)
    }
  }, [phase, currentPendingIndex, voiceMode])

  // ── Speak first pending question when voice mode is toggled on ────
  useEffect(() => {
    if (!voiceMode || !config || loadingConfig) return
    if (phase === 'chat' && pendingQuestions[currentPendingIndex]) {
      speak(t('chatbot.askField', { field: pendingQuestions[currentPendingIndex].label }), () =>
        startListening()
      )
    } else if (phase === 'initial') {
      voiceAccumulatedRef.current = ''
      speak(t('chatbot.describePrompt'), () => startListening())
    } else if (phase === 'reviewing') {
      speak(t('chatbot.reviewPrompt'), () => startListening())
    } else if (phase === 'review-input') {
      voiceAccumulatedRef.current = ''
      speak(t('chatbot.reviewInputPrompt'), () => startListening())
    } else if (phase === 'summary') {
      speak(t('chatbot.summaryVoiceHint'), () => startListening())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceMode])

  // ── Helpers ───────────────────────────────────────────────────────
  const addMessage = (role: 'bot' | 'user', text: string) => {
    setMessages((prev) => [...prev, { role, text }])
  }

  const buildSummary = (collected: CollectedAnswers, questions: ChatbotQuestion[]): string => {
    const lines = questions.map((q) => `• ${q.label}: ${collected[q.id] || '—'}`)
    return t('chatbot.summaryIntro') + '\n' + lines.join('\n')
  }

  // ── Helper: transition to 'summary' phase (with optional voice listen) ──
  const goToSummaryPhase = (finalAnswers: CollectedAnswers) => {
    setAnswers(finalAnswers)
    answersRef.current = finalAnswers
    setPhase('summary')
    setInputValue('')
    const summaryMsg = buildSummary(finalAnswers, config!.questions)
    const confirmMsg = t('chatbot.confirmPrompt')
    setTimeout(() => {
      addMessage('bot', summaryMsg)
      addMessage('bot', confirmMsg)
      // Voice: skip reading the full field list, just ask for confirmation
      if (voiceModeRef.current) speak(confirmMsg, () => startListening())
    }, 300)
  }

  // ── Helper: transition to 'reviewing' phase ───────────────────────
  const goToReviewing = (finalAnswers: CollectedAnswers) => {
    setAnswers(finalAnswers)
    answersRef.current = finalAnswers
    setPhase('reviewing')
    setInputValue('')
    const summaryMsg = buildSummary(finalAnswers, config!.questions)
    const prompt = t('chatbot.reviewPrompt')
    setTimeout(() => {
      addMessage('bot', summaryMsg)
      addMessage('bot', prompt)
      // Voice: skip reading the full field list, just ask the review question
      if (voiceModeRef.current) speak(prompt, () => startListening())
    }, 300)
  }

  // ── Helper: run extraction from a modification text (review-input or direct) ──
  const runReviewExtraction = async (text: string) => {
    if (!config) return
    stopListening()
    voiceAccumulatedRef.current = ''
    addMessage('user', text)
    setReviewInput('')
    setPhase('review-extracting')
    const extractingMsg = t('chatbot.reviewExtracting')
    addMessage('bot', extractingMsg)
    if (voiceModeRef.current) speak(extractingMsg)
    try {
      const extracted: Record<string, string | null> = await api.extractChatbotFields(
        text,
        config.questions
      )
      const updatedAnswers = { ...answersRef.current }
      const updatedLines: string[] = []
      for (const q of config.questions) {
        const val = extracted[q.id] ?? null
        if (val) {
          updatedAnswers[q.id] = val
          updatedLines.push(`• ${q.label}: ${val}`)
        }
      }
      if (updatedLines.length === 0) {
        // No value extracted — user probably mentioned a field without giving a new value
        // (e.g. "peux-tu changer l'heure"). Ask them to provide the actual value.
        const askMsg = t('chatbot.reviewNoValueFound')
        addMessage('bot', askMsg)
        setPhase('summary')
        if (voiceModeRef.current) speak(askMsg, () => startListening())
        return
      }
      const msg = t('chatbot.reviewUpdated') + '\n' + updatedLines.join('\n')
      addMessage('bot', msg)
      if (voiceModeRef.current) speak(t('chatbot.reviewUpdated'))
      answersRef.current = updatedAnswers
      setAnswers(updatedAnswers)
      const summaryMsg = buildSummary(updatedAnswers, config.questions)
      const confirmMsg = t('chatbot.confirmPrompt')
      setPhase('summary')
      setTimeout(() => {
        addMessage('bot', summaryMsg)
        addMessage('bot', confirmMsg)
        if (voiceModeRef.current) speak(confirmMsg)
      }, 300)
    } catch {
      const summaryMsg = buildSummary(answersRef.current, config.questions)
      const confirmMsg = t('chatbot.confirmPrompt')
      setPhase('summary')
      setTimeout(() => {
        addMessage('bot', summaryMsg)
        addMessage('bot', confirmMsg)
        if (voiceModeRef.current) speak(confirmMsg)
      }, 300)
    }
  }
  useEffect(() => {
    runReviewExtractionRef.current = runReviewExtraction
  })

  // ── Phase: reviewing — user confirms or wants to add/modify ───────
  const handleReviewChoice = (yes: boolean, content?: string) => {
    stopListening()
    if (yes) {
      // If "yes" came with meaningful extra content (e.g. "Oui, la référence est 4525"),
      // extract directly — no need to ask the user to repeat themselves.
      const extra = content ? content.replace(/^(oui|yes)[,.\s]*/i, '').trim() : ''
      if (extra.length > 3) {
        runReviewExtractionRef.current(content!)
        return
      }
      addMessage('user', t('chatbot.reviewYes'))
      voiceAccumulatedRef.current = ''
      setReviewInput('')
      setPhase('review-input')
      const prompt = t('chatbot.reviewInputPrompt')
      setTimeout(() => {
        addMessage('bot', prompt)
        if (voiceModeRef.current) speak(prompt, () => startListening())
      }, 300)
    } else {
      addMessage('user', t('chatbot.reviewNo'))
      goToSummaryPhase(answersRef.current)
    }
  }
  useEffect(() => {
    handleReviewChoiceRef.current = handleReviewChoice
  })

  // ── Phase: review-input — delegates to the shared extraction helper ──
  const handleReviewInputSubmit = async () => {
    const text = reviewInput.trim()
    if (!text) return
    await runReviewExtractionRef.current(text)
  }
  useEffect(() => {
    handleReviewInputSubmitRef.current = handleReviewInputSubmit
  })

  // Voice answer for 'reviewing' phase: detect oui/non + optional inline content
  useEffect(() => {
    handleReviewVoiceAnswerRef.current = () => {
      const val = inputValue.trim()
      // Only "oui/yes" count as a definitive yes — verbs like "changer/modifier" alone
      // are ambiguous (e.g. "peux-tu changer l'heure") and should not trigger extraction
      const isYes = /\b(oui|yes)\b/i.test(val)
      if (isYes) {
        // Check whether the user provided content beyond just the yes keyword
        // e.g. "Oui, la référence est 4525" → extract directly
        const extra = val.replace(/^(oui|yes)[,.\s]*/i, '').trim()
        if (extra.length > 3) {
          handleReviewChoiceRef.current(true, val)
        } else {
          handleReviewChoiceRef.current(true)
        }
      } else {
        // Treat unclear/empty response as "no"
        handleReviewChoiceRef.current(false)
      }
    }
  }, [inputValue])

  // ── Phase: initial description submitted ──────────────────────────
  const handleDescriptionSubmit = async () => {
    const rawDesc = initialDescription.trim()
    if (!rawDesc || !config) return
    stopListening()
    voiceAccumulatedRef.current = ''

    // Normalize: capitalize first letter and ensure terminal punctuation
    let desc = rawDesc.charAt(0).toUpperCase() + rawDesc.slice(1)
    if (!/[.!?]$/.test(desc)) desc += '.'

    // Persist normalized description for ticket body construction
    setRawDescription(desc)

    addMessage('user', desc)
    setInitialDescription('')
    setPhase('extracting')
    // No bot message during extraction — the spinner provides visual feedback

    try {
      const extracted: Record<string, string | null> =
        config.questions.length > 0 ? await api.extractChatbotFields(desc, config.questions) : {}

      // Build initial answers from extracted values (keyed by question id)
      const newAnswers: CollectedAnswers = {}
      const pending: ChatbotQuestion[] = []
      const filledLines: string[] = []

      for (const q of config.questions) {
        const val = extracted[q.id] ?? null
        if (val) {
          newAnswers[q.id] = val
          filledLines.push(`• ${q.label}: ${val}`)
        } else {
          pending.push(q)
        }
      }

      setAnswers(newAnswers)

      if (filledLines.length > 0) {
        const extractMsg = t('chatbot.extractedFields') + '\n' + filledLines.join('\n')
        addMessage('bot', extractMsg)
      }

      if (pending.length === 0) {
        // All fields extracted — say thanks then go to review
        const merciMsg = t('chatbot.missingFields')
        addMessage('bot', merciMsg)
        if (voiceModeRef.current) speak(merciMsg)
        setPendingQuestions([])
        setTimeout(() => goToReviewing(newAnswers), 300)
      } else {
        // Ask missing questions
        setPendingQuestions(pending)
        setCurrentPendingIndex(0)
        const missingMsg =
          pending.length < config.questions.length
            ? t('chatbot.missingFields')
            : t('chatbot.noExtraction')
        setPhase('chat')
        setTimeout(() => {
          addMessage('bot', missingMsg)
          const firstQ = t('chatbot.askField', { field: pending[0].label })
          addMessage('bot', firstQ)
          if (voiceModeRef.current) {
            speak(missingMsg + ' ' + firstQ, () => startListening())
          }
        }, 300)
      }
    } catch {
      // Extraction failed: ask all questions sequentially
      const pending = [...(config?.questions ?? [])]
      setPendingQuestions(pending)
      setCurrentPendingIndex(0)
      setPhase('chat')
      const errMsg = t('chatbot.extractionFailed')
      setTimeout(() => {
        addMessage('bot', errMsg)
        if (pending[0]) {
          const firstQ = t('chatbot.askField', { field: pending[0].label })
          addMessage('bot', firstQ)
          if (voiceModeRef.current) speak(errMsg + ' ' + firstQ, () => startListening())
        }
      }, 300)
    }
  }

  // ── Phase: chat — answer a pending question ────────────────────────
  const handleSend = useCallback(() => {
    const currentQ = pendingQuestions[currentPendingIndex]
    if (!currentQ || !config) return
    const rawValue = inputValue.trim()
    if (!rawValue && currentQ.required) return

    // Validate typed/spoken value against the question's expected data type
    let value = rawValue
    if (rawValue) {
      const validation = validateAnswer(currentQ, rawValue)
      if (!validation.valid) {
        const errMsg = t(validation.errorKey ?? 'chatbot.validationGeneric')
        addMessage('bot', `⚠️ ${errMsg}`)
        const reaskQ = t('chatbot.askField', { field: currentQ.label })
        addMessage('bot', reaskQ)
        setInputValue('')
        if (voiceModeRef.current) speak(`${errMsg}. ${reaskQ}`, () => startListening())
        return // do not advance — re-ask the same question
      }
      value = validation.normalized // e.g. "8h30" → "08:30"
    }

    stopListening()
    addMessage('user', value || '—')
    setInputValue('')

    // Key by question id (not field) — multiple questions can share the same ERP field
    // Use ref to avoid stale closure — answers state may lag behind when handleSend is captured
    const updatedAnswers = { ...answersRef.current, [currentQ.id]: value }
    setAnswers(updatedAnswers)
    answersRef.current = updatedAnswers

    const nextIndex = currentPendingIndex + 1

    if (nextIndex < pendingQuestions.length) {
      setCurrentPendingIndex(nextIndex)
      const nextQ = pendingQuestions[nextIndex]
      setTimeout(() => {
        const nextQMsg = t('chatbot.askField', { field: nextQ.label })
        addMessage('bot', nextQMsg)
        if (voiceModeRef.current) speak(nextQMsg, () => startListening())
      }, 300)
    } else if (skipReviewPhaseRef.current) {
      // Pencil-edit from summary: skip review, return directly to summary
      skipReviewPhaseRef.current = false
      goToSummaryPhase(updatedAnswers)
    } else {
      // All pending questions answered — go to review step before summary
      goToReviewing(updatedAnswers)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingQuestions,
    currentPendingIndex,
    config,
    inputValue,
    speak,
    startListening,
    stopListening,
    t,
  ])

  useEffect(() => {
    handleSendRef.current = handleSend
  }, [handleSend])
  const handleDescriptionSubmitRef = useRef(handleDescriptionSubmit)
  useEffect(() => {
    handleDescriptionSubmitRef.current = handleDescriptionSubmit
  })

  // Voice handler for summary phase: "confirmer/ok" → confirm, anything else → review-input
  useEffect(() => {
    handleSummaryVoiceRef.current = () => {
      const val = inputValue.toLowerCase().trim()
      const isConfirm =
        /\b(confirm|confirme|envoyer|envoie|oui|yes|ok|correct|parfait|bien|valider|valide)\b/.test(
          val
        )
      if (isConfirm) {
        handleConfirmRef.current()
      } else if (val.length > 0) {
        // User spoke a correction from the summary — extract directly without extra step
        runReviewExtractionRef.current(val)
      }
    }
  }, [inputValue])

  useEffect(() => {
    if (phase === 'initial') handleSendRef.current = () => handleDescriptionSubmitRef.current()
    else if (phase === 'reviewing')
      handleSendRef.current = () => handleReviewVoiceAnswerRef.current()
    else if (phase === 'review-input')
      handleSendRef.current = () => handleReviewInputSubmitRef.current()
    else if (phase === 'summary') handleSendRef.current = () => handleSummaryVoiceRef.current()
    else handleSendRef.current = handleSend
  }, [phase, handleSend])

  // ── Submit ticket ─────────────────────────────────────────────────
  const handleConfirm = async () => {
    stopListening()
    setPhase('submitting')
    setSubmitError(null)
    try {
      // Map question-id-keyed answers back to ERP field values for ticket creation.
      // When multiple questions map to the same field, answers are joined with newlines.
      const fieldValues: Record<string, string[]> = {}
      for (const q of config?.questions ?? []) {
        const val = answers[q.id]
        if (val) {
          if (!fieldValues[q.field]) fieldValues[q.field] = []
          fieldValues[q.field].push(val)
        }
      }
      const fv = (f: string) => (fieldValues[f] ?? []).join('\n')

      // Build message body: raw description first, then Q&A answers
      const qAndALines: string[] = []
      for (const q of config?.questions ?? []) {
        const val = answers[q.id]
        if (val) qAndALines.push(`${q.label}: ${val}`)
      }
      const messageBody =
        [rawDescription, ...(qAndALines.length > 0 ? ['', ...qAndALines] : [])].join('\n').trim() ||
        fv('message')

      await api.createTicket({
        subject: fv('subject') || t('chatbot.defaultSubject'),
        message: messageBody,
        type_code: fv('type_code') || '',
        category_code: fv('category_code') || '',
        severity_code: fv('severity_code') || '',
      })
      setPhase('done')
      const msg = t('chatbot.ticketCreated')
      addMessage('bot', msg)
      if (voiceModeRef.current) speak(msg)
    } catch {
      setPhase('summary')
      setSubmitError(t('chatbot.submitError'))
      if (voiceModeRef.current) speak(t('chatbot.submitError'))
    }
  }
  useEffect(() => {
    handleConfirmRef.current = handleConfirm
  })

  const handleRestart = () => {
    if (!config) return
    stopListening()
    voiceAccumulatedRef.current = ''
    setAnswers({})
    setInitialDescription('')
    setRawDescription('')
    setReviewInput('')
    setPhase('initial')
    setSubmitError(null)
    const restartMsg = t('chatbot.restart')
    const descPrompt = t('chatbot.describePrompt')
    setMessages((prev) => [
      ...prev,
      { role: 'bot', text: restartMsg },
      { role: 'bot', text: descPrompt },
    ])
    // Voice: only read the describe prompt, restartMsg is already shown in chat
    if (voiceModeRef.current) speak(descPrompt)
  }

  // Re-ask a single question so the user can correct their answer
  const handleEditQuestion = useCallback(
    (questionIndex: number) => {
      if (!config) return
      const q = config.questions[questionIndex]
      stopListening()
      // Pre-fill input with the existing answer so it is easy to edit
      setInputValue(answers[q.id] || '')
      setPendingQuestions([q])
      setCurrentPendingIndex(0)
      skipReviewPhaseRef.current = true // return to summary directly after answering
      setPhase('chat')
      const editPrompt = `${t('chatbot.editAnswer')} : ${q.label}`
      addMessage('bot', editPrompt)
      if (voiceModeRef.current) speak(editPrompt, () => startListening())
    },
    [config, answers, stopListening, speak, startListening, t]
  )

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (phase === 'initial') handleDescriptionSubmit()
      else if (phase === 'chat') handleSend()
      else if (phase === 'review-input') handleReviewInputSubmitRef.current()
    }
  }

  const handleMicClick = () => {
    if (isListening) stopListening()
    else startListening(true) // force=true: manual click works even without voice mode
  }

  // ── Current pending question (chat phase) ─────────────────────────
  const currentQ = pendingQuestions[currentPendingIndex] ?? null

  // ── Progress calculation (total questions asked incl. extraction) ──
  const totalQuestions = config?.questions.length ?? 0
  const answeredByExtraction = totalQuestions - pendingQuestions.length
  const answeredByUser = currentPendingIndex
  const totalAnswered = answeredByExtraction + answeredByUser

  // ── Input area for 'initial' and 'chat' phases ────────────────────
  const renderInput = () => {
    if (phase === 'initial') {
      return (
        <div className="flex gap-2">
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={initialDescription}
            onChange={(e) => setInitialDescription(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            placeholder={
              isListening ? t('chatbot.listeningPlaceholder') : t('chatbot.descriptionPlaceholder')
            }
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none text-sm transition-colors ${
              isListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
          />
          <div className="flex flex-col gap-1 self-end">
            {voiceMode && <MicButton isListening={isListening} onClick={handleMicClick} />}
            <button
              onClick={handleDescriptionSubmit}
              disabled={!initialDescription.trim()}
              className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )
    }

    if (phase === 'chat' && currentQ) {
      if (currentQ.type === 'select' && currentQ.options?.length) {
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {currentQ.options.map((opt) => (
                <button
                  key={opt}
                  onClick={() => {
                    setInputValue(opt)
                    setTimeout(() => handleSendRef.current(), 50)
                  }}
                  className="px-4 py-2 bg-primary-50 text-primary-700 border border-primary-200 rounded-full text-sm hover:bg-primary-100 transition-colors"
                >
                  {opt}
                </button>
              ))}
              {!currentQ.required && (
                <button
                  onClick={() => {
                    setInputValue('')
                    setTimeout(() => handleSendRef.current(), 50)
                  }}
                  className="px-4 py-2 bg-gray-50 text-gray-500 border border-gray-200 rounded-full text-sm hover:bg-gray-100 transition-colors"
                >
                  {t('chatbot.skip')}
                </button>
              )}
            </div>
            {voiceMode && (
              <div className="flex justify-center">
                <MicButton isListening={isListening} onClick={handleMicClick} />
              </div>
            )}
          </div>
        )
      }

      const isTextarea = currentQ.type === 'textarea'
      const InputTag = isTextarea ? 'textarea' : 'input'
      // Map question type to HTML input type and placeholder
      const inputTypeMap: Record<string, string> = {
        date: 'date',
        time: 'text',
        number: 'number',
        email: 'email',
        phone: 'tel',
        text: 'text',
        textarea: 'text',
      }
      const placeholderMap: Record<string, string> = {
        time: t('chatbot.placeholderTime'),
        number: t('chatbot.placeholderNumber'),
        email: t('chatbot.placeholderEmail'),
        phone: t('chatbot.placeholderPhone'),
      }
      const htmlInputType = inputTypeMap[currentQ.type] ?? 'text'
      const typePlaceholder = placeholderMap[currentQ.type] ?? t('chatbot.inputPlaceholder')
      return (
        <div className="flex gap-2">
          <InputTag
            ref={inputRef as React.RefObject<HTMLTextAreaElement & HTMLInputElement>}
            type={htmlInputType}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={isTextarea ? 3 : undefined}
            placeholder={isListening ? t('chatbot.listeningPlaceholder') : typePlaceholder}
            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm transition-colors ${
              isListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
            } ${isTextarea ? 'resize-none' : ''}`}
          />
          <div className={`flex ${isTextarea ? 'flex-col' : ''} gap-1 self-end`}>
            {voiceMode && <MicButton isListening={isListening} onClick={handleMicClick} />}
            {!currentQ.required && !voiceMode && (
              <button
                onClick={() => {
                  setInputValue('')
                  setTimeout(() => handleSendRef.current(), 50)
                }}
                className="px-3 py-2 text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm"
              >
                {t('chatbot.skip')}
              </button>
            )}
            <button
              onClick={handleSend}
              disabled={currentQ.required && !inputValue.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )
    }

    return null
  }

  // ── Main render ───────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col"
        style={{ maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-gradient-to-r from-primary-600 to-primary-700 rounded-t-2xl">
          <div
            className={`w-9 h-9 bg-white bg-opacity-20 rounded-full flex items-center justify-center transition-all ${
              isSpeaking ? 'ring-2 ring-white ring-opacity-60 animate-pulse' : ''
            }`}
          >
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-white font-semibold text-sm">{t('chatbot.title')}</p>
            <p className="text-primary-100 text-xs">
              {isSpeaking
                ? t('chatbot.statusSpeaking')
                : isListening
                  ? t('chatbot.statusListening')
                  : phase === 'extracting'
                    ? t('chatbot.statusAnalysing')
                    : t('chatbot.subtitle')}
            </p>
          </div>
          {voiceSupported && (
            <button
              onClick={toggleVoiceMode}
              title={voiceMode ? t('chatbot.voiceOff') : t('chatbot.voiceOn')}
              className={`p-2 rounded-lg transition-colors ${
                voiceMode
                  ? 'bg-white bg-opacity-30 text-white'
                  : 'bg-white bg-opacity-10 text-primary-200 hover:bg-opacity-20 hover:text-white'
              }`}
            >
              {voiceMode ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
            </button>
          )}
          <button
            onClick={() => {
              window.speechSynthesis?.cancel()
              stopListening()
              onClose()
            }}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>

        {/* Voice banner */}
        {voiceMode && (
          <div className="bg-indigo-50 border-b border-indigo-100 px-4 py-2 flex items-center gap-2 text-xs text-indigo-700">
            {isListening ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                {t('chatbot.statusListening')}
              </>
            ) : isSpeaking ? (
              <>
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
                </span>
                {t('chatbot.statusSpeaking')}
              </>
            ) : (
              <>
                <Volume2 className="w-3 h-3" />
                {t('chatbot.voiceActive')}
              </>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loadingConfig ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : (
            messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div
                  className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center ${
                    msg.role === 'bot' ? 'bg-primary-100' : 'bg-gray-200'
                  }`}
                >
                  {msg.role === 'bot' ? (
                    <Bot className="w-4 h-4 text-primary-600" />
                  ) : (
                    <User className="w-4 h-4 text-gray-600" />
                  )}
                </div>
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                    msg.role === 'bot'
                      ? 'bg-gray-100 text-gray-800 rounded-tl-sm'
                      : 'bg-primary-600 text-white rounded-tr-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Progress bar */}
        {(phase === 'chat' || phase === 'extracting') && totalQuestions > 0 && (
          <div className="px-4 py-1">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>
                {phase === 'extracting'
                  ? t('chatbot.statusAnalysing')
                  : t('chatbot.question', { current: totalAnswered + 1, total: totalQuestions })}
              </span>
              {answeredByExtraction > 0 && (
                <span className="flex items-center gap-1 text-indigo-500">
                  <Sparkles className="w-3 h-3" />
                  {t('chatbot.autoFilled', { count: answeredByExtraction })}
                </span>
              )}
            </div>
            <div className="h-1 bg-gray-200 rounded-full">
              <div
                className={`h-1 rounded-full transition-all ${
                  phase === 'extracting' ? 'bg-indigo-400 animate-pulse w-full' : 'bg-primary-500'
                }`}
                style={
                  phase !== 'extracting'
                    ? { width: `${(totalAnswered / totalQuestions) * 100}%` }
                    : undefined
                }
              />
            </div>
          </div>
        )}

        {/* Input area */}
        <div className="p-4 border-t border-gray-200">
          {submitError && <p className="text-red-600 text-xs mb-2">{submitError}</p>}

          {(phase === 'initial' || phase === 'chat') && renderInput()}

          {(phase === 'extracting' || phase === 'review-extracting') && (
            <div className="flex items-center justify-center gap-2 text-indigo-600 py-2">
              <Sparkles className="w-4 h-4 animate-pulse" />
              <span className="text-sm">{t('chatbot.statusAnalysing')}</span>
            </div>
          )}

          {phase === 'reviewing' && (
            <div className="space-y-2">
              {voiceMode && (
                <div className="flex justify-center mb-1">
                  <MicButton isListening={isListening} onClick={handleMicClick} />
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleReviewChoiceRef.current(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm transition-colors"
                >
                  {t('chatbot.reviewNo')}
                </button>
                <button
                  onClick={() => handleReviewChoiceRef.current(true)}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm transition-colors"
                >
                  {t('chatbot.reviewYes')}
                </button>
              </div>
              {/* Direct modification input — skip the extra "please describe" step */}
              <div className="flex gap-2 pt-1">
                <input
                  type="text"
                  value={reviewInput}
                  onChange={(e) => setReviewInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && reviewInput.trim()) {
                      e.preventDefault()
                      runReviewExtractionRef.current(reviewInput.trim())
                    }
                  }}
                  placeholder={t('chatbot.reviewDirectPlaceholder')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                />
                <button
                  onClick={() => {
                    if (reviewInput.trim()) runReviewExtractionRef.current(reviewInput.trim())
                  }}
                  disabled={!reviewInput.trim()}
                  className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {phase === 'review-input' && (
            <div className="flex gap-2">
              <textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={reviewInput}
                onChange={(e) => setReviewInput(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={3}
                placeholder={
                  isListening
                    ? t('chatbot.listeningPlaceholder')
                    : t('chatbot.reviewInputPlaceholder')
                }
                className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none text-sm transition-colors ${
                  isListening ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              <div className="flex flex-col gap-1 self-end">
                {voiceMode && <MicButton isListening={isListening} onClick={handleMicClick} />}
                <button
                  onClick={() => handleReviewInputSubmitRef.current()}
                  disabled={!reviewInput.trim()}
                  className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {phase === 'summary' && (
            <div className="space-y-3">
              {/* Editable answers list — one row per question with a pencil button */}
              {config && (
                <div className="space-y-1 max-h-44 overflow-y-auto pr-1">
                  {config.questions.map((q, idx) => (
                    <div
                      key={q.id}
                      className="flex items-start gap-2 py-1 border-b border-gray-100 last:border-0"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 leading-tight">{q.label}</p>
                        <p className="text-sm font-medium text-gray-800 break-words">
                          {answers[q.id] || '—'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleEditQuestion(idx)}
                        title={t('chatbot.editAnswer')}
                        className="flex-shrink-0 p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {/* Free-text correction input — user can describe a change in natural language */}
              <div className="flex gap-2">
                {voiceMode && <MicButton isListening={isListening} onClick={handleMicClick} />}
                <input
                  type="text"
                  value={reviewInput}
                  onChange={(e) => setReviewInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && reviewInput.trim()) {
                      e.preventDefault()
                      runReviewExtractionRef.current(reviewInput.trim())
                    }
                  }}
                  placeholder={t('chatbot.summaryCorrectPlaceholder')}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none text-sm"
                />
                <button
                  onClick={() => {
                    if (reviewInput.trim()) runReviewExtractionRef.current(reviewInput.trim())
                  }}
                  disabled={!reviewInput.trim()}
                  className="px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-40 transition-colors"
                  title={t('chatbot.editAnswer')}
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={handleRestart}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm transition-colors"
                >
                  {t('chatbot.modify')}
                </button>
                <button
                  onClick={handleConfirm}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg shadow hover:shadow-md text-sm transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-4 h-4" />
                  {t('chatbot.confirm')}
                </button>
              </div>
            </div>
          )}

          {phase === 'submitting' && (
            <div className="flex items-center justify-center gap-2 text-primary-600 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{t('chatbot.submitting')}</span>
            </div>
          )}

          {phase === 'done' && (
            <button
              onClick={() => {
                onTicketCreated()
                onClose()
              }}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {t('chatbot.close')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ------------------------------------------------------------------
// Mic button sub-component
// ------------------------------------------------------------------
function MicButton({ isListening, onClick }: { isListening: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative p-2 rounded-lg transition-colors ${
        isListening
          ? 'bg-red-100 text-red-600 hover:bg-red-200'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      title={isListening ? "Arrêter l'écoute" : 'Parler'}
    >
      {isListening && (
        <span className="absolute inset-0 rounded-lg animate-ping bg-red-200 opacity-60" />
      )}
      {isListening ? <MicOff className="w-4 h-4 relative z-10" /> : <Mic className="w-4 h-4" />}
    </button>
  )
}
